import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatTriggerSource } from '@cotera/api';
import {
  ModelScopeFactory,
  useWatchableValue,
  type Model,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import type { UrlParamAdapter } from '@cotera/client/app/etc/url-param-watchable';
import {
  ChatHistoryListPageModel,
  useChatHistoryListPageModel,
  type ConversationListItem,
} from '../chat/models/chat-history-list-page.model';
import {
  AGENT_CHAT_COLUMNS,
  ChatListView,
} from '../chat/components/chat-list-view';
import {
  CHAT_TRIGGER_SOURCES,
  CHAT_TRIGGER_SOURCE_LABELS,
  resolveAgentTriggerSources,
} from '../chat/utils/chat-trigger-source';
import type { FilterOption } from '../resources/list/resource-list-view';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';

/**
 * Embedded chat lists keep their list params (search/page) in memory rather than
 * the URL, so they don't write `?search/page/sort` into the agent editor's URL.
 */
const NOOP_URL_ADAPTER: UrlParamAdapter = {
  setParam: () => {},
  setParams: () => {},
};

const AgentChatsTabContent: React.FC = () => {
  const org = useWhoami((s) => s.org);
  const navigate = useNavigate();
  const listPageModel = useChatHistoryListPageModel();

  const listParams = useWatchableValue(listPageModel.listParamsWatchable);
  const { page, limit, search, triggerSources } = listParams;
  const chatsData = useWatchableValue(listPageModel.chatsDataWatchable);

  const conversations = chatsData?.items ?? [];
  const total = chatsData?.total ?? 0;
  const hasMore = total > limit * page;
  const status = chatsData === null ? 'loading' : 'ready';

  // An empty selection queries every source, so resolve it before rendering the
  // chips — otherwise they all read as unchecked while everything is listed, and
  // unchecking one would send `[]` (= all) and visibly snap back.
  const effectiveTriggerSources = useMemo(
    () => resolveAgentTriggerSources(triggerSources),
    [triggerSources]
  );

  const toggleTriggerSource = useCallback(
    (source: ChatTriggerSource, checked: boolean) => {
      const next = checked
        ? [...effectiveTriggerSources, source]
        : effectiveTriggerSources.filter((value) => value !== source);
      listPageModel.setTriggerSources(next);
    },
    [listPageModel, effectiveTriggerSources]
  );

  const filterOptions: FilterOption[] = useMemo(
    () =>
      CHAT_TRIGGER_SOURCES.map((source) => ({
        label: CHAT_TRIGGER_SOURCE_LABELS[source],
        key: source,
        checked: effectiveTriggerSources.includes(source),
        onChange: (checked: boolean) => toggleTriggerSource(source, checked),
      })),
    [effectiveTriggerSources, toggleTriggerSource]
  );

  const handleLoadPage = useCallback(
    (p: number, take: number) => listPageModel.loadPage(p, take),
    [listPageModel]
  );
  const handleSearchChange = useCallback(
    (value: string) => listPageModel.setSearch(value),
    [listPageModel]
  );
  const handleClickConversation = useCallback(
    (conversation: ConversationListItem) => {
      navigate(`/${org.slug}/chats/${conversation.id}`);
    },
    [navigate, org.slug]
  );

  return (
    <ChatListView
      layout="embedded"
      // The tab above this list already says "Chats"; the serif heading spent
      // the pane's first 36px saying it again. Only the count stays.
      hideTitle
      // The editor column owns the gutter (`AGENT_CONTENT_INSET`), so the list
      // adds none. Its default `px-8` used to stack on the wrapper's, putting
      // the counter on a different left edge to the header above it.
      containerClassName="px-0"
      items={conversations}
      totalCount={total}
      status={status}
      onClickConversation={handleClickConversation}
      searchQuery={search}
      onSearchQueryChange={handleSearchChange}
      filterOptions={filterOptions}
      columnLayout="agent"
      columns={AGENT_CHAT_COLUMNS}
      hasMore={hasMore}
      loadPage={handleLoadPage}
      page={page}
      take={limit}
      emptyText="No chats with this agent yet"
      noMatchText="No chats match your search"
      searchPlaceholder="Search chats..."
    />
  );
};

/**
 * Agent editor "Chats" tab: lists the chats linked to a saved agent. Reuses the
 * standalone chat-history list model (scoped to `agentId`, in-memory params) and
 * the shared `ChatListView`, so it stays in sync with the main chat list.
 */
export const AgentChatsTab: React.FC<{ agentId: string }> = ({ agentId }) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const watchablesFactory = useWatchablesFactory();

  const createModels = useCallback(
    (): Model[] => [
      new ChatHistoryListPageModel({
        orgId,
        queryClient,
        watchablesFactory,
        urlAdapter: NOOP_URL_ADAPTER,
        initialSearchParams: new URLSearchParams(),
        client,
        agentId,
      }),
    ],
    [orgId, queryClient, watchablesFactory, client, agentId]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[client, queryClient, orgId, watchablesFactory, agentId]}
    >
      <AgentChatsTabContent />
    </ModelScopeFactory>
  );
};
